import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Snackbar,
  Grid,
  Paper,
  Chip,
} from '@mui/material';
import { Save, Refresh } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  PageHeader,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { formatIndianNumber } from '../../utils/currency';
import crmPlusService, { ForecastSubmission, ForecastAccuracyRow } from '../../services/crmPlusService';

const money = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : formatIndianNumber(v);

const formatPeriod = (start: string | null | undefined, end?: string | null) => {
  if (!start) return '—';
  const s = new Date(start).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  if (!end) return s;
  const e = new Date(end).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  return `${s} – ${e}`;
};

interface EditableForecast {
  commitAmount: string;
  bestCaseAmount: string;
  pipelineAmount: string;
  notes: string;
}

const toEditable = (f: ForecastSubmission): EditableForecast => ({
  commitAmount: f.commitAmount !== null && f.commitAmount !== undefined ? String(f.commitAmount) : '',
  bestCaseAmount: f.bestCaseAmount !== null && f.bestCaseAmount !== undefined ? String(f.bestCaseAmount) : '',
  pipelineAmount: f.pipelineAmount !== null && f.pipelineAmount !== undefined ? String(f.pipelineAmount) : '',
  notes: f.notes || '',
});

const ForecastSubmissionPage: React.FC = () => {
  const chartTokens = useChartTokens();

  const [myForecast, setMyForecast] = useState<ForecastSubmission | null>(null);
  const [myLoading, setMyLoading] = useState(true);
  const [myError, setMyError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditableForecast>({ commitAmount: '', bestCaseAmount: '', pipelineAmount: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const [submissions, setSubmissions] = useState<ForecastSubmission[]>([]);
  const [subLoading, setSubLoading] = useState(true);
  const [subError, setSubError] = useState<string | null>(null);

  const [accuracy, setAccuracy] = useState<ForecastAccuracyRow[]>([]);
  const [accLoading, setAccLoading] = useState(true);
  const [accError, setAccError] = useState<string | null>(null);

  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const fetchMyForecast = useCallback(async () => {
    setMyLoading(true);
    setMyError(null);
    try {
      const result = await crmPlusService.getMyForecast();
      setMyForecast(result);
      setEditForm(toEditable(result));
    } catch (err: any) {
      setMyError(err.message || 'Failed to load your forecast');
    } finally {
      setMyLoading(false);
    }
  }, []);

  const fetchSubmissions = useCallback(async () => {
    setSubLoading(true);
    setSubError(null);
    try {
      const result = await crmPlusService.getForecastSubmissions();
      setSubmissions(result.submissions || []);
    } catch (err: any) {
      setSubError(err.message || 'Failed to load team submissions');
    } finally {
      setSubLoading(false);
    }
  }, []);

  const fetchAccuracy = useCallback(async () => {
    setAccLoading(true);
    setAccError(null);
    try {
      const result = await crmPlusService.getForecastAccuracy();
      const rows = Array.isArray(result) ? result : result?.accuracy || [];
      setAccuracy(rows);
    } catch (err: any) {
      setAccError(err.message || 'Failed to load forecast accuracy');
    } finally {
      setAccLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyForecast();
    fetchSubmissions();
    fetchAccuracy();
  }, [fetchMyForecast, fetchSubmissions, fetchAccuracy]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await crmPlusService.upsertForecast({
        commitAmount: editForm.commitAmount.trim() ? parseFloat(editForm.commitAmount) : 0,
        bestCaseAmount: editForm.bestCaseAmount.trim() ? parseFloat(editForm.bestCaseAmount) : 0,
        pipelineAmount: editForm.pipelineAmount.trim() ? parseFloat(editForm.pipelineAmount) : 0,
        notes: editForm.notes || undefined,
      });
      const saved = result?.submission || result;
      if (saved) {
        setMyForecast(saved as ForecastSubmission);
        setEditForm(toEditable(saved as ForecastSubmission));
      }
      setToast({ message: 'Forecast saved', severity: 'success' });
      fetchSubmissions();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to save forecast', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const accuracyChartData = useMemo(
    () =>
      accuracy.map((r) => ({
        label: `${r.userName} — ${formatPeriod(r.periodStart)}`,
        commit: r.commitAmount || 0,
        actualWon: r.actualWon || 0,
      })),
    [accuracy]
  );

  const isDirty = useMemo(() => {
    if (!myForecast) return false;
    const base = toEditable(myForecast);
    return (
      base.commitAmount !== editForm.commitAmount ||
      base.bestCaseAmount !== editForm.bestCaseAmount ||
      base.pipelineAmount !== editForm.pipelineAmount ||
      base.notes !== editForm.notes
    );
  }, [myForecast, editForm]);

  return (
    <Box>
      <PageHeader
        title="Forecast Submission"
        subtitle="Your monthly commit, team submissions and historical accuracy"
        actions={
          <IconButton
            onClick={() => {
              fetchMyForecast();
              fetchSubmissions();
              fetchAccuracy();
            }}
            disabled={myLoading || subLoading || accLoading}
          >
            <Refresh />
          </IconButton>
        }
      />

      {/* My Forecast */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            My Forecast — {myForecast ? formatPeriod(myForecast.periodStart, myForecast.periodEnd) : 'This Month'}
          </Typography>
          {myForecast?.isDraft && (
            <Chip label="Draft — prefilled from your pipeline" size="small" color="info" variant="outlined" />
          )}
        </Box>

        {myLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : myError ? (
          <ErrorState message={myError} onRetry={fetchMyForecast} />
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Commit Amount"
                  type="number"
                  value={editForm.commitAmount}
                  onChange={(e) => setEditForm((f) => ({ ...f, commitAmount: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Best Case Amount"
                  type="number"
                  value={editForm.bestCaseAmount}
                  onChange={(e) => setEditForm((f) => ({ ...f, bestCaseAmount: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Pipeline Amount"
                  type="number"
                  value={editForm.pipelineAmount}
                  onChange={(e) => setEditForm((f) => ({ ...f, pipelineAmount: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving || !isDirty}>
                {saving ? 'Saving…' : 'Save Forecast'}
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {/* Team Submissions */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        Team Submissions
      </Typography>
      {subError ? (
        <TableSurface>
          <ErrorState message={subError} onRetry={fetchSubmissions} />
        </TableSurface>
      ) : (
        <TableSurface>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>User</TableCell>
                  <TableCell sx={headerCellSx}>Period</TableCell>
                  <TableCell sx={headerCellSx} align="right">Commit</TableCell>
                  <TableCell sx={headerCellSx} align="right">Best Case</TableCell>
                  <TableCell sx={headerCellSx} align="right">Pipeline</TableCell>
                  <TableCell sx={headerCellSx}>Submitted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, border: 0 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : submissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ border: 0 }}>
                      <TableEmptyState title="No forecast submissions yet" hint="Team commits appear here once submitted." />
                    </TableCell>
                  </TableRow>
                ) : (
                  submissions.map((s) => (
                    <TableRow key={s.id ?? `${s.userId}-${s.periodStart}`} sx={bodyRowSx}>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {s.userName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatPeriod(s.periodStart, s.periodEnd)}</TableCell>
                      <TableCell align="right">{money(s.commitAmount)}</TableCell>
                      <TableCell align="right">{money(s.bestCaseAmount)}</TableCell>
                      <TableCell align="right">{money(s.pipelineAmount)}</TableCell>
                      <TableCell>
                        {s.submittedAt ? new Date(s.submittedAt).toLocaleDateString('en-IN') : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* Forecast Accuracy */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 4, mb: 1.5 }}>
        Forecast Accuracy
      </Typography>
      {accError ? (
        <TableSurface>
          <ErrorState message={accError} onRetry={fetchAccuracy} />
        </TableSurface>
      ) : (
        <>
          <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              Committed vs. Actual Won
            </Typography>
            {accLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            ) : accuracyChartData.length === 0 ? (
              <TableEmptyState title="No past submissions yet" hint="Accuracy compares a real past commit to the deals that actually closed — nothing to show until a period closes." />
            ) : (
              <Box sx={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={accuracyChartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                    <CartesianGrid {...gridProps(chartTokens)} />
                    <XAxis dataKey="label" {...axisProps(chartTokens)} hide />
                    <YAxis {...axisProps(chartTokens)} tickFormatter={(v) => formatIndianNumber(v)} width={70} />
                    <Tooltip {...tooltipStyle(chartTokens)} formatter={(v: number) => formatIndianNumber(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="commit" name="Committed" fill={chartTokens.categorical[0]} radius={[4, 4, 0, 0]} maxBarSize={40} />
                    <Bar dataKey="actualWon" name="Actual Won" fill={chartTokens.categorical[1]} radius={[4, 4, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

          <TableSurface>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>User</TableCell>
                    <TableCell sx={headerCellSx}>Period</TableCell>
                    <TableCell sx={headerCellSx} align="right">Commit</TableCell>
                    <TableCell sx={headerCellSx} align="right">Best Case</TableCell>
                    <TableCell sx={headerCellSx} align="right">Actual Won</TableCell>
                    <TableCell sx={headerCellSx} align="right">Accuracy %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6, border: 0 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : accuracy.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ border: 0 }}>
                        <TableEmptyState title="No accuracy data yet" hint="Only real past submissions with a closed period appear here." />
                      </TableCell>
                    </TableRow>
                  ) : (
                    accuracy.map((r) => (
                      <TableRow key={r.submissionId} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {r.userName}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatPeriod(r.periodStart, r.periodEnd)}</TableCell>
                        <TableCell align="right">{money(r.commitAmount)}</TableCell>
                        <TableCell align="right">{money(r.bestCaseAmount)}</TableCell>
                        <TableCell align="right">{money(r.actualWon)}</TableCell>
                        <TableCell align="right">
                          {r.accuracyPct === null || r.accuracyPct === undefined ? (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                              {r.accuracyPct.toFixed(1)}%
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default ForecastSubmissionPage;
